import React, { useState, useEffect, useMemo } from 'react';
import { 
  Plus, 
  Filter, 
  Search, 
  Edit, 
  Trash2, 
  Calendar, 
  User, 
  Copy,
  FileText,
  Settings,
  X
} from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import Input from '../components/ui/Input';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../utils/formatters';
import { formatArgentinaDate } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import ErrorModal from '../components/modals/ErrorModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import { useAppStore } from '../store';

// Define types
interface QuoteInputType {
  id: string;
  name: string;
  created_at: string;
}

interface QuoteInput {
  id: string;
  input_type_id: string;
  input_name: string;
  supplier: string;
  internal_cost: number;
  margin: number;
  final_price: number;
  yield: number | null;
  price_per_meter: number | null;
  is_active: boolean;
  quote_input_types?: {
    name: string;
  };
}

interface QuoteItem {
  id: string;
  input: QuoteInput;
  consumption: number;
  cost: number;
  include_in_text: boolean;
  customInputName?: string;
  customPrice?: number;
  isCustomInput?: boolean;
}

interface Quote {
  id: string;
  article_name: string;
  client: string;
  salesperson: string;
  quote_text: string;
  total_price: number;
  created_at: string;
}

const Cotizador: React.FC = () => {
  const { isAdmin, userRole, user } = useAuth();
  const navigate = useNavigate();
  const { toast, ToastComponent } = useToast();
  const { clients, salespersons } = useAppStore();
  
  // Redirect if not admin or sales
  useEffect(() => {
    if (userRole && userRole !== 'Administrador' && userRole !== 'Vendedor') {
      navigate('/');
    }
  }, [userRole, navigate]);

  // State for quote input types
  const [inputTypes, setInputTypes] = useState<QuoteInputType[]>([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  
  // State for quote inputs
  const [quoteInputs, setQuoteInputs] = useState<QuoteInput[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  
  // State for current quote
  const [articleName, setArticleName] = useState('');
  const [client, setClient] = useState('');
  const [salesperson, setSalesperson] = useState('');
  const [quoteItems, setQuoteItems] = useState<QuoteItem[]>([]);
  const [quoteText, setQuoteText] = useState('');
  const [totalPrice, setTotalPrice] = useState(0);
  
  // State for quote history filters
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [articleFilter, setArticleFilter] = useState<string>('');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [salespersonFilter, setSalespersonFilter] = useState<string>('all');
  
  // State for viewing quote details
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  
  // State for delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState<string | null>(null);
  
  // Fetch quote input types
  const fetchQuoteInputTypes = async () => {
    try {
      setIsLoadingTypes(true);
      
      const { data, error } = await supabase
        .from('quote_input_types')
        .select('*')
        .order('name');
      
      if (error) throw error;
      
      setInputTypes(data || []);
    } catch (error: any) {
      console.error('Error fetching quote input types:', error);
      setError(error.message || 'Error al cargar los tipos de insumos');
      setShowErrorModal(true);
    } finally {
      setIsLoadingTypes(false);
    }
  };

  // Set default salesperson when salespersons are loaded
  useEffect(() => {
    if (salespersons.length > 0 && !salesperson) {
      setSalesperson(salespersons[0].name);
    }
  }, [salespersons, salesperson]);
  
  // Fetch quote inputs
  const fetchQuoteInputs = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('quote_inputs')
        .select('*, quote_input_types(name)')
        .order('input_name');
      
      if (error) throw error;
      
      setQuoteInputs(data || []);
    } catch (error: any) {
      console.error('Error fetching quote inputs:', error);
      setError(error.message || 'Error al cargar los insumos');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch quotes
  const fetchQuotes = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setQuotes(data || []);
    } catch (error: any) {
      console.error('Error fetching quotes:', error);
      setError(error.message || 'Error al cargar las cotizaciones');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // State for quotes
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [isLoadingQuotes, setIsLoadingQuotes] = useState(false);
  
  // Fetch data on component mount
  useEffect(() => {
    fetchQuoteInputTypes();
    fetchQuoteInputs();
    fetchQuotes();
  }, []);
  
  // Get available inputs for a specific input type
  const getInputsByTypeId = (typeId: string) => {
    return quoteInputs.filter(input => input.input_type_id === typeId);
  };
  
  // Get available suppliers for a specific input type
  const getSuppliersByTypeId = (typeId: string) => {
    const inputs = getInputsByTypeId(typeId);
    const suppliers = [...new Set(inputs.map(input => input.supplier))];
    return suppliers.map(supplier => ({ value: supplier, label: supplier }));
  };
  
  // Get inputs for a specific type and supplier
  const getInputsByTypeAndSupplier = (typeId: string, supplier: string) => {
    return quoteInputs.filter(input => 
      input.input_type_id === typeId && 
      input.supplier === supplier
    );
  };
  
  // Check if input type is fabric
  const isTelaType = (id: string): boolean => {
    const type = inputTypes.find(t => t.id === id);
    const typeName = type ? type.name.toLowerCase() : '';
    return typeName === 'tela';
  };
  
  // Add a new quote item
  const handleAddItem = () => {
    // Create new item with empty fields
    const newItem: QuoteItem = {
      id: Date.now().toString(), // Temporary ID
      input: {
        id: '',
        input_type_id: '',
        input_name: '',
        supplier: '',
        internal_cost: 0,
        margin: 0,
        final_price: 0,
        yield: null,
        price_per_meter: null,
        is_active: true
      },
      consumption: 1,
      cost: 0,
      include_in_text: true,
      isCustomInput: false,
      customInputName: '',
      customPrice: 0
    };
    
    setQuoteItems([...quoteItems, newItem]);
  };
  
  // Remove quote item
  const handleRemoveItem = (id: string) => {
    const updatedItems = quoteItems.filter(item => item.id !== id);
    setQuoteItems(updatedItems);
  };
  
  // Update item
  const handleUpdateItem = (index: number, field: string, value: any) => {
    // Create a new array to avoid mutating the original
    const updatedItems = [...quoteItems];
    const item = { ...updatedItems[index] };
    
    if (field === 'input_type_id') {
      // Check if the selected type is "Otro"
      if (value === 'custom') {
        // Set up a custom input
        item.isCustomInput = true;
        item.customInputName = '';
        item.customPrice = 0;
        item.cost = 0;
        item.input = {
          ...item.input,
          input_type_id: value,
          input_name: 'Otro',
          final_price: 0
        };
      } else {
        // Regular input type handling
        const isTela = isTelaType(value);
        
        // For tela type, we need to handle suppliers first
        if (isTela) {
          const suppliers = getSuppliersByTypeId(value);
          if (suppliers.length > 0) {
            const firstSupplier = suppliers[0].value;
            const inputs = getInputsByTypeAndSupplier(value, firstSupplier);
            
            if (inputs.length > 0) {
              // Update the input
              item.input = inputs[0];
              
              // Calculate cost based on input type
              const cost = inputs[0].price_per_meter
                ? inputs[0].price_per_meter * item.consumption
                : inputs[0].final_price;
              
              item.cost = cost;
            } else {
              // Just update the input_type_id
              item.input = {
                ...item.input,
                input_type_id: value
              };
            }
          } else {
            // Just update the input_type_id
            item.input = {
              ...item.input,
              input_type_id: value
            };
          }
        } else {
          // For non-tela types, just get the inputs directly
          const inputs = getInputsByTypeId(value);
          
          if (inputs.length > 0) {
            // Update the input
            item.input = inputs[0];
            
            // Set cost to final_price for non-tela items
            item.cost = inputs[0].final_price;
          } else {
            // Just update the input_type_id
            item.input = {
              ...item.input,
              input_type_id: value
            };
          }
        }
        
        // Reset custom input flags
        item.isCustomInput = false;
        item.customInputName = '';
        item.customPrice = 0;
      }
    } else if (field === 'supplier') {
      // When supplier changes, update available inputs
      const inputs = getInputsByTypeAndSupplier(item.input.input_type_id, value);
      
      if (inputs.length > 0) {
        // Update the input
        item.input = inputs[0];
        
        // Calculate cost based on input type
        const isTela = isTelaType(item.input.input_type_id);
        const cost = isTela && item.input.price_per_meter
          ? item.input.price_per_meter * item.consumption
          : item.input.final_price;
        
        item.cost = cost;
      } else {
        // Just update the supplier
        item.input = {
          ...item.input,
          supplier: value
        };
      }
    } else if (field === 'input_id') {
      // Find the selected input
      const selectedInput = quoteInputs.find(input => input.id === value);
      if (selectedInput) {
        // Update the input
        item.input = selectedInput;
        
        // Calculate cost based on input type
        const isTela = isTelaType(selectedInput.input_type_id);
        const cost = isTela && selectedInput.price_per_meter
          ? selectedInput.price_per_meter * item.consumption
          : selectedInput.final_price;
        
        item.cost = cost;
      }
    } else if (field === 'consumption') {
      // Update consumption
      item.consumption = parseFloat(value) || 0;
      
      // Recalculate cost for fabric
      if (!item.isCustomInput) {
        const isTela = isTelaType(item.input.input_type_id);
        const cost = isTela && item.input.price_per_meter
          ? item.input.price_per_meter * item.consumption
          : item.input.final_price;
        
        item.cost = cost;
      }
    } else if (field === 'include_in_text') {
      item.include_in_text = value;
    } else if (field === 'customInputName') {
      item.customInputName = value;
    } else if (field === 'customPrice') {
      item.customPrice = parseFloat(value) || 0;
      item.cost = parseFloat(value) || 0;
    }
    
    updatedItems[index] = item;
    setQuoteItems(updatedItems);
  };
  
  // Calculate total price (excluding moldería and muestra)
  const totalPriceCalculated = useMemo(() => {
    return quoteItems
      .filter(item => {
        const typeName = item.input.quote_input_types?.name || '';
        return typeName.toLowerCase() !== 'moldería' && typeName.toLowerCase() !== 'muestra';
      })
      .reduce((sum, item) => sum + item.cost, 0);
  }, [quoteItems]);
  
  // Update total price when items change
  useEffect(() => {
    setTotalPrice(totalPriceCalculated);
  }, [totalPriceCalculated]);
  
  // Check if we have a moldería item
  const hasMolderiaItem = useMemo(() => {
    return quoteItems.some(item => {
      const typeName = item.input.quote_input_types?.name || '';
      return typeName.toLowerCase() === 'moldería' || 
        item.input.input_name.toLowerCase().includes('moldería');
    });
  }, [quoteItems]);
  
  // Get moldería cost
  const molderiaCost = useMemo(() => {
    const molderiaItem = quoteItems.find(item => {
      const typeName = item.input.quote_input_types?.name || '';
      return typeName.toLowerCase() === 'moldería' || 
        item.input.input_name.toLowerCase().includes('moldería');
    });
    
    if (molderiaItem) {
      return molderiaItem.cost;
    }
    
    return totalPrice * 0.5; // Default is 50% of total price
  }, [quoteItems, totalPrice]);
  
  // Check if we have a muestra item
  const hasMuestraItem = useMemo(() => {
    return quoteItems.some(item => {
      const typeName = item.input.quote_input_types?.name || '';
      return typeName.toLowerCase() === 'muestra' || 
        item.input.input_name.toLowerCase().includes('muestra');
    });
  }, [quoteItems]);
  
  // Get muestra cost
  const muestraCost = useMemo(() => {
    const muestraItem = quoteItems.find(item => {
      const typeName = item.input.quote_input_types?.name || '';
      return typeName.toLowerCase() === 'muestra' || 
        item.input.input_name.toLowerCase().includes('muestra');
    });
    
    if (muestraItem) {
      return muestraItem.cost;
    }
    
    return totalPrice * 2; // Default is 200% of total price
  }, [quoteItems, totalPrice]);
  
  // Generate quote text
  const generateQuoteText = () => {
    if (!articleName) {
      setError('Debe ingresar un nombre de artículo');
      setShowErrorModal(true);
      return;
    }
    
    // Find fabric item for the title
    const fabricItem = quoteItems.find(item => {
      const typeName = item.input.quote_input_types?.name || '';
      return typeName.toLowerCase() === 'tela' && item.include_in_text;
    });
    
    // Generate text
    let text = `*${articleName.toUpperCase()}${fabricItem ? ` (${fabricItem.input.input_name})` : ''}*\n`;
    
    // Define the standard line items
    const standardItems = [
      'Corte',
      'Confección',
      'Corte y confección',
      'Serigrafía',
      'DTF',
      'Bordado',
      'Avios',
      'Control de calidad',
      'Planchado y embolsado'
    ];
    
    // Add each standard item if it exists in the quote items
    standardItems.forEach(standardItem => {
      // Check if there's a matching item that should be included in text
      const matchingItem = quoteItems.find(item => {
        const typeName = item.input.quote_input_types?.name || '';
        return item.include_in_text && 
          (typeName.toLowerCase() === standardItem.toLowerCase() ||
           item.input.input_name.toLowerCase() === standardItem.toLowerCase() ||
           (item.isCustomInput && item.customInputName.toLowerCase() === standardItem.toLowerCase()));
      });
      
      if (matchingItem) {
        text += `- ${standardItem}\n`;
      }
    });
    
    // Add custom items that don't match standard items
    quoteItems.forEach(item => {
      if (item.include_in_text && item.isCustomInput && item.customInputName) {
        // Check if this custom item is not already included in standard items
        const isStandardItem = standardItems.some(
          standardItem => standardItem.toLowerCase() === item.customInputName.toLowerCase()
        );
        
        if (!isStandardItem) {
          text += `- ${item.customInputName}\n`;
        }
      }
    });
    
    // Add value
    text += `*Valor unitario: ${formatCurrency(totalPrice)}*\n\n`;
    
    // Add moldería and muestra values only if they exist
    if (hasMolderiaItem) {
      text += `Valor moldería: ${formatCurrency(molderiaCost)}\n`;
    }
    
    if (hasMuestraItem) {
      text += `Valor muestra: ${formatCurrency(muestraCost)}`;
    }
    
    setQuoteText(text);
  };
  
  // Save quote
  const handleSaveQuote = async () => {
    if (!articleName) {
      setError('Debe ingresar un nombre de artículo');
      setShowErrorModal(true);
      return;
    }
    
    if (!client) {
      setError('Debe ingresar un cliente');
      setShowErrorModal(true);
      return;
    }
    
    if (quoteItems.length === 0) {
      setError('Debe agregar al menos un ítem a la cotización');
      setShowErrorModal(true);
      return;
    }

    try {
      // Generate quote text if not already generated
      let text = quoteText;
      if (!text) {
        // Find fabric item for the title
        const fabricItem = quoteItems.find(item => {
          const typeName = item.input.quote_input_types?.name || '';
          return typeName.toLowerCase() === 'tela' && item.include_in_text;
        });
        
        // Generate text
        text = `*${articleName.toUpperCase()}${fabricItem ? ` (${fabricItem.input.input_name})` : ''}*\n`;
        
        // Define the standard line items
        const standardItems = [
          'Corte',
          'Confección',
          'Corte y confección',    
          'Serigrafía',
          'DTF',
          'Bordado',
          'Avios',
          'Control de calidad',
          'Planchado y embolsado'
        ];
        
        // Add each standard item if it exists in the quote items
        standardItems.forEach(standardItem => {
          // Check if there's a matching item that should be included in text
          const matchingItem = quoteItems.find(item => {
            const typeName = item.input.quote_input_types?.name || '';
            return item.include_in_text && 
              (typeName.toLowerCase() === standardItem.toLowerCase() ||
               item.input.input_name.toLowerCase() === standardItem.toLowerCase() ||
               (item.isCustomInput && item.customInputName.toLowerCase() === standardItem.toLowerCase()));
          });
          
          if (matchingItem) {
            text += `- ${standardItem}\n`;
          }
        });
        
        // Add custom items that don't match standard items
        quoteItems.forEach(item => {
          if (item.include_in_text && item.isCustomInput && item.customInputName) {
            // Check if this custom item is not already included in standard items
            const isStandardItem = standardItems.some(
              standardItem => standardItem.toLowerCase() === item.customInputName.toLowerCase()
            );
            
            if (!isStandardItem) {
              text += `- ${item.customInputName}\n`;
            }
          }
        });
        
        // Add value
        text += `*Valor unitario: ${formatCurrency(totalPrice)}*\n\n`;
        
        // Add moldería and muestra values only if they exist
        if (hasMolderiaItem) {
          text += `Valor moldería: ${formatCurrency(molderiaCost)}\n`;
        }
        
        if (hasMuestraItem) {
          text += `Valor muestra: ${formatCurrency(muestraCost)}`;
        }
      }
      
      // Insert quote
      const { data: quoteData, error: quoteError } = await supabase
        .from('quotes')
        .insert([{
          article_name: articleName,
          client: client,
          salesperson: salesperson,
          quote_text: text,
          total_price: totalPrice
        }])
        .select()
        .single();
      
      if (quoteError) throw quoteError;
      
      // Insert quote items
      const quoteItems_data = quoteItems.map(item => {
        // For custom inputs, we need to handle them specially
        if (item.isCustomInput) {
          return {
            quote_id: quoteData.id,
            input_id: null, // No reference to an existing input
            consumption: item.consumption,
            cost: item.cost,
            include_in_text: item.include_in_text,
            custom_input_name: item.customInputName,
            custom_price: item.customPrice
          };
        } else {
          return {
            quote_id: quoteData.id,
            input_id: item.input.id,
            consumption: item.consumption,
            cost: item.cost,
            include_in_text: item.include_in_text
          };
        }
      });
      
      const { error: itemsError } = await supabase
        .from('quote_items')
        .insert(quoteItems_data);
      
      if (itemsError) throw itemsError;
      
      // Log activity
      await supabase
        .from('activity_logs')
        .insert([{
          user_id: user?.id,
          action: 'Creación de cotización',
          entity_type: 'quote',
          entity_id: quoteData.id,
          details: `Cotización creada para artículo: ${articleName} - Cliente: ${client}`,
          action_type: 'create'
        }]);
      
      // Show success message
      toast.success('Cotización guardada', 'La cotización ha sido guardada correctamente');
      
      // Reset form
      setArticleName('');
      setClient('');
      setSalesperson('');
      setQuoteItems([]);
      setQuoteText('');
      setTotalPrice(0);
      
      // Refresh quotes
      fetchQuotes();
    } catch (error: any) {
      console.error('Error saving quote:', error);
      setError(error.message || 'Error al guardar la cotización');
      setShowErrorModal(true);
    }
  };
  
  // Copy quote text to clipboard
  const handleCopyText = () => {
    navigator.clipboard.writeText(quoteText);
    toast.success('Texto copiado', 'El texto de la cotización ha sido copiado al portapapeles');
    
    // Log activity
    supabase
      .from('activity_logs')
      .insert([{
        user_id: user?.id,
        action: 'Copia de texto de cotización',
        entity_type: 'quote',
        entity_id: null,
        details: `Texto de cotización copiado para artículo: ${articleName}`,
        action_type: 'view'
      }]);
  };
  
  // Delete quote
  const handleDeleteQuote = async () => {
    if (!quoteToDelete) return;
    
    try {
      const { error } = await supabase
        .from('quotes')
        .delete()
        .eq('id', quoteToDelete);
      
      if (error) throw error;
      
      toast.success('Cotización eliminada', 'La cotización ha sido eliminada correctamente');
      
      // Refresh quotes
      fetchQuotes();
      
      // Close modal
      setDeleteConfirmOpen(false);
      setQuoteToDelete(null);
    } catch (error: any) {
      console.error('Error deleting quote:', error);
      setError(error.message || 'Error al eliminar la cotización');
      setShowErrorModal(true);
    }
  };
  
  // Filter quotes
  const filteredQuotes = useMemo(() => {
    return quotes.filter(quote => {
      // Filter by date range
      if (dateFilter === 'custom' && startDate && endDate) {
        const quoteDate = new Date(quote.created_at);
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59); // Include the entire end day
        
        if (quoteDate < start || quoteDate > end) {
          return false;
        }
      }
      
      // Filter by article name
      if (articleFilter && !quote.article_name.toLowerCase().includes(articleFilter.toLowerCase())) {
        return false;
      }
      
      // Filter by client
      if (clientFilter && !quote.client.toLowerCase().includes(clientFilter.toLowerCase())) {
        return false;
      }
      
      // Filter by salesperson
      if (salespersonFilter !== 'all' && quote.salesperson !== salespersonFilter) {
        return false;
      }
      
      return true;
    });
  }, [quotes, dateFilter, startDate, endDate, articleFilter, clientFilter, salespersonFilter]);
  
  // Reset filters
  const handleResetFilters = () => {
    setDateFilter('all');
    setStartDate('');
    setEndDate('');
    setArticleFilter('');
    setClientFilter('');
    setSalespersonFilter('all');
  };
  
  // View quote details
  const handleViewQuote = (quote: Quote) => {
    setSelectedQuote(quote);
    
    // Log activity
    supabase
      .from('activity_logs')
      .insert([{
        user_id: user?.id,
        action: 'Visualización de cotización',
        entity_type: 'quote',
        entity_id: quote.id,
        details: `Cotización visualizada: ${quote.article_name} - Cliente: ${quote.client}`,
        action_type: 'view'
      }]);
  };
  
  // Close quote details
  const handleCloseQuoteDetails = () => {
    setSelectedQuote(null);
  };
  
  // Navigate to quote inputs configuration
  const handleGoToConfig = () => {
    navigate('/configuracion/cotizador');
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center">
          <FileText className="mr-2 h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          Cotizador
        </h2>
        
        {isAdmin && (
          <Button
            variant="outline"
            icon={<Settings className="h-5 w-5" />}
            onClick={handleGoToConfig}
          >
            Configuración
          </Button>
        )}
      </div>
      
      {/* New Quote Form */}
      <Card>
        <CardHeader title="Nueva Cotización" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Input
              label="Nombre del Artículo"
              id="article_name"
              name="article_name"
              value={articleName}
              onChange={(e) => setArticleName(e.target.value)}
              required
            />
            
            <Input
              label="Cliente"
              id="client"
              name="client"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              required
            />
            
            <Select
              label="Vendedor"
              value={salesperson}
              onChange={setSalesperson}
              options={salespersons.map(person => ({ value: person.name, label: person.name }))}
              required
            />
          </div>
          
          {/* Quote Items */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Ítems</h3>
              <Button
                variant="outline"
                size="sm"
                icon={<Plus className="h-4 w-4" />}
                onClick={handleAddItem}
              >
                Agregar Ítem
              </Button>
            </div>
            
            {quoteItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
                No hay ítems en la cotización. Haga clic en "Agregar Ítem" para comenzar.
              </div>
            ) : (
              <div className="space-y-4">
                {quoteItems.map((item, index) => {
                  // Get input type options with "Otro" option
                  const inputTypeOptions = [
                    ...inputTypes.map(type => ({
                      value: type.id,
                      label: type.name
                    })),
                    { value: 'custom', label: 'Otro' }
                  ];
                  
                  // Check if this is a custom input
                  if (item.isCustomInput) {
                    return (
                      <div 
                        key={item.id} 
                        className="grid grid-cols-12 gap-2 items-center border border-gray-200 dark:border-gray-700 p-4 rounded-lg"
                      >
                        <div className="col-span-2">
                          <Select
                            label="Tipo"
                            value={item.input.input_type_id}
                            onChange={(value) => handleUpdateItem(index, 'input_type_id', value)}
                            options={inputTypeOptions}
                          />
                        </div>
                        
                        <div className="col-span-4">
                          <Input
                            label="Nombre del Insumo"
                            id={`customInputName-${index}`}
                            name={`customInputName-${index}`}
                            value={item.customInputName || ''}
                            onChange={(e) => handleUpdateItem(index, 'customInputName', e.target.value)}
                            required
                          />
                        </div>
                        
                        <div className="col-span-2">
                          <Input
                            label="Precio"
                            type="number"
                            id={`customPrice-${index}`}
                            name={`customPrice-${index}`}
                            value={item.customPrice?.toString() || '0'}
                            onChange={(e) => handleUpdateItem(index, 'customPrice', parseFloat(e.target.value))}
                            min="0"
                            step="0.01"
                            required
                          />
                        </div>
                        
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Precio
                          </label>
                          <div className="h-10 px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-800 flex items-center">
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {formatCurrency(item.cost)}
                            </span>
                          </div>
                        </div>
                        
                        <div className="col-span-1 flex items-end justify-between h-full">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={item.include_in_text}
                              onChange={(e) => handleUpdateItem(index, 'include_in_text', e.target.checked)}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">Incluir</span>
                          </div>
                          
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(item.id)}
                            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    );
                  }
                  
                  // Regular input handling
                  const isTela = item.input.input_type_id && isTelaType(item.input.input_type_id);
                  
                  return (
                    <div 
                      key={item.id} 
                      className="grid grid-cols-12 gap-2 items-center border border-gray-200 dark:border-gray-700 p-4 rounded-lg"
                    >
                      <div className="col-span-2">
                        <Select
                          label="Tipo"
                          value={item.input.input_type_id}
                          onChange={(value) => handleUpdateItem(index, 'input_type_id', value)}
                          options={inputTypeOptions}
                        />
                      </div>
                      
                      {isTela ? (
                        <>
                          <div className="col-span-2">
                            <Select
                              label="Proveedor"
                              value={item.input.supplier}
                              onChange={(value) => handleUpdateItem(index, 'supplier', value)}
                              options={getSuppliersByTypeId(item.input.input_type_id)}
                            />
                          </div>
                          
                          <div className="col-span-2">
                            <Select
                              label="Insumo"
                              value={item.input.id}
                              onChange={(value) => handleUpdateItem(index, 'input_id', value)}
                              options={getInputsByTypeAndSupplier(item.input.input_type_id, item.input.supplier).map(input => ({
                                value: input.id,
                                label: input.input_name
                              }))}
                            />
                          </div>
                          
                          <div className="col-span-2">
                            <Input
                              label="Consumo (metros)"
                              type="number"
                              id={`consumption-${index}`}
                              name={`consumption-${index}`}
                              value={item.consumption.toString()}
                              onChange={(e) => handleUpdateItem(index, 'consumption', parseFloat(e.target.value))}
                              min="0.01"
                              step="0.01"
                              required
                            />
                          </div>
                        </>
                      ) : (
                        <div className="col-span-6">
                          <Select
                            label="Insumo"
                            value={item.input.id}
                            onChange={(value) => handleUpdateItem(index, 'input_id', value)}
                            options={item.input.input_type_id ? 
                              getInputsByTypeId(item.input.input_type_id).map(input => ({
                                value: input.id,
                                label: input.input_name
                              })) : []
                            }
                          />
                        </div>
                      )}
                      
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Precio
                        </label>
                        <div className="h-10 px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-800 flex items-center">
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {formatCurrency(item.cost)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="col-span-1 flex items-end justify-between h-full">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={item.include_in_text}
                            onChange={(e) => handleUpdateItem(index, 'include_in_text', e.target.checked)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">Incluir</span>
                        </div>
                        
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(item.id)}
                          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Quote Text */}
          {quoteText && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Texto de Cotización</h3>
                <Button
                  variant="outline"
                  size="sm"
                  icon={<Copy className="h-4 w-4" />}
                  onClick={handleCopyText}
                >
                  Copiar
                </Button>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{quoteText}</pre>
              </div>
              <div className="mt-2 text-right">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Total: {formatCurrency(totalPrice)}
                </p>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={generateQuoteText}
              disabled={quoteItems.length === 0 || !articleName || !client}
            >
              Generar Texto
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveQuote}
              disabled={quoteItems.length === 0 || !articleName || !client}
            >
              Guardar Cotización
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Quote History */}
      <Card>
        <CardHeader title="Historial de Cotizaciones" />
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Select
              label="Período"
              value={dateFilter}
              onChange={(value) => {
                setDateFilter(value);
                if (value !== 'custom') {
                  setStartDate('');
                  setEndDate('');
                }
              }}
              options={[
                { value: 'all', label: 'Todas las fechas' },
                { value: 'custom', label: 'Personalizado' },
              ]}
            />
            
            {dateFilter === 'custom' && (
              <>
                <Input
                  label="Desde"
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <Input
                  label="Hasta"
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </>
            )}
            
            <div className="relative">
              <Input
                label="Artículo"
                id="articleFilter"
                name="articleFilter"
                value={articleFilter}
                onChange={(e) => setArticleFilter(e.target.value)}
                placeholder="Buscar por nombre de artículo..."
              />
              <div className="absolute right-3 top-9">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            
            <div className="relative">
              <Input
                label="Cliente"
                id="clientFilter"
                name="clientFilter"
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                placeholder="Buscar por cliente..."
              />
              <div className="absolute right-3 top-9">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            
            <Select
              label="Vendedor"
              value={salespersonFilter}
              onChange={setSalespersonFilter}
              options={[
                { value: 'all', label: 'Todos' },
                ...salespersons.map(person => ({ value: person.name, label: person.name }))
              ]}
            />
            
            <div className="flex items-end">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={handleResetFilters}
              >
                Limpiar Filtros
              </Button>
            </div>
          </div>
          
          {/* Quotes Table */}
          {isLoadingQuotes ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
            </div>
          ) : filteredQuotes.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No se encontraron cotizaciones con los filtros seleccionados
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Artículo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Vendedor
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Precio Total
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredQuotes.map((quote) => (
                    <tr key={quote.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatArgentinaDate(quote.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {quote.article_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {quote.client}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {quote.salesperson}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-white">
                        {formatCurrency(quote.total_price)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<FileText className="h-4 w-4" />}
                            onClick={() => handleViewQuote(quote)}
                          >
                            Ver
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<Trash2 className="h-4 w-4" />}
                            onClick={() => {
                              setQuoteToDelete(quote.id);
                              setDeleteConfirmOpen(true);
                            }}
                          >
                            Eliminar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Quote Details Modal */}
      {selectedQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Detalle de Cotización
                </h3>
                <button
                  onClick={handleCloseQuoteDetails}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Fecha: {formatArgentinaDate(selectedQuote.created_at)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Cliente: {selectedQuote.client}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Vendedor: {selectedQuote.salesperson}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Artículo: {selectedQuote.article_name}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Precio Total: {formatCurrency(selectedQuote.total_price)}
                </p>
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">Texto de Cotización</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<Copy className="h-4 w-4" />}
                    onClick={() => {
                      navigator.clipboard.writeText(selectedQuote.quote_text);
                      toast.success('Texto copiado', 'El texto de la cotización ha sido copiado al portapapeles');
                      
                      // Log activity
                      supabase
                        .from('activity_logs')
                        .insert([{
                          user_id: user?.id,
                          action: 'Copia de texto de cotización',
                          entity_type: 'quote',
                          entity_id: selectedQuote.id,
                          details: `Texto de cotización copiado: ${selectedQuote.article_name} - Cliente: ${selectedQuote.client}`,
                          action_type: 'view'
                        }]);
                    }}
                  >
                    Copiar
                  </Button>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{selectedQuote.quote_text}</pre>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={handleCloseQuoteDetails}
                >
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setQuoteToDelete(null);
        }}
        onConfirm={handleDeleteQuote}
        title="Eliminar Cotización"
        message="¿Estás seguro de que deseas eliminar esta cotización? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />
      
      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error inesperado'}
      />
      
      {/* Toast Component */}
      {ToastComponent}
    </div>
  );
};

export default Cotizador;